/**
 * #467 — ordering for a pulled MLS delivery batch.
 *
 * The server assigns a per-group total order (`mls_messages.seq`) and hands it
 * to us on every delivery row. Our job is to preserve it, not to re-derive one.
 *
 * The previous inline sort in `_pullDeliveryQueue` grouped by MESSAGE TYPE
 * first — every welcome, then every commit, then every application — and only
 * then by `created_at`. Two consequences, both live:
 *
 *   1. Type-grouping hoists ALL commits above ALL application messages. A batch
 *      of commit@N+1, app@N+1, commit@N+2 therefore applied both commits before
 *      decrypting the application message, which was encrypted at epoch N+1 and
 *      is unreadable once the group reaches N+2. Deterministic, not a race —
 *      #474's resync ladder absorbed it, which is why it never surfaced alone.
 *   2. `new Date(...).getTime()` truncates Postgres microseconds to
 *      milliseconds, so same-millisecond messages fell back to arrival order.
 *
 * Welcomes still sort first, and that is not a type-grouping relic: a Welcome
 * bootstraps group state, and a commit cannot be applied to a group this device
 * has not joined yet. Everything after that follows the server.
 */
export interface DeliveryOrderable {
  message_type?: string;
  seq?: number | string | null;
  created_at?: string;
}

/** Numeric seq, or null when the server did not send one (version skew). */
function seqOf(m: DeliveryOrderable): number | null {
  if (m.seq === undefined || m.seq === null || m.seq === "") return null;
  const n = Number(m.seq);
  return Number.isFinite(n) ? n : null;
}

function createdAtOf(m: DeliveryOrderable): number {
  const t = new Date(m.created_at ?? 0).getTime();
  return Number.isFinite(t) ? t : 0;
}

/**
 * Order a delivery batch: Welcomes first, then strict server order.
 *
 * Returns a new array; the caller's is left untouched. `Array.prototype.sort`
 * is stable (ES2019+), so entries the server could not distinguish keep the
 * order it sent them in.
 */
export function orderDeliveryBatch<T extends DeliveryOrderable>(messages: T[]): T[] {
  return [...messages].sort((a, b) => {
    const aWelcome = a.message_type === "welcome";
    const bWelcome = b.message_type === "welcome";
    if (aWelcome !== bWelcome) return aWelcome ? -1 : 1;

    // Server order. Both sides must carry a seq for it to mean anything —
    // comparing a seq against a missing one would order by accident.
    const as = seqOf(a);
    const bs = seqOf(b);
    if (as !== null && bs !== null && as !== bs) return as - bs;

    // Server too old to send seq: degrade to the previous behaviour rather
    // than to an arbitrary order.
    return createdAtOf(a) - createdAtOf(b);
  });
}
